import { BehaviorSubject, Subject, fromEvent, merge, Observable } from 'rxjs';
import { delay, filter, take, throttleTime, takeUntil } from 'rxjs/operators';
import * as uuid from 'uuid';

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  OnInit,
  OnDestroy,
  Attribute,
  Component,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';

import { SnScrollService, SnScrollReference } from '@lib/common/services';

@Component({
  selector: 'sn-infinite-scroll',
  templateUrl: './infinite-scroll.component.html',
  styleUrls: ['./infinite-scroll.component.scss'],
  host: { class: 'sn-infinite-scroll--block' },
})
export class SnInfiniteScrollComponent implements OnInit, OnDestroy {
  public isLoading: boolean;

  private readonly _element: HTMLElement;
  private readonly _scrollRef: SnScrollReference;
  private _id: string;

  private _complete = false;

  private readonly _load$ = new Subject<void>();
  private readonly _loaded$ = new Subject<boolean>();
  private readonly _loading$ = new BehaviorSubject<boolean>(false);

  private readonly _destroy$ = new Subject<void>();

  constructor(
    elementRef: ElementRef,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _scrollService: SnScrollService,
    @Attribute('windowScrollTarget') windowScrollTarget: boolean,
  ) {
    this._id = uuid.v4();
    windowScrollTarget = coerceBooleanProperty(windowScrollTarget);

    this._element = windowScrollTarget ? document.documentElement : elementRef.nativeElement;

    this._scrollRef = this._scrollService.create(
      `fs-infinite-scroll-${this._id}`,
      windowScrollTarget ? document : elementRef.nativeElement,
    );
  }

  get load$(): Observable<void> {
    return this._load$.asObservable();
  }

  get isComplete(): boolean {
    return this._complete;
  }

  public ngOnInit(): void {
    this._initSubscriptions();
    this.reload();
  }

  public ngOnDestroy(): void {
    this._load$.complete();
    this._loaded$.complete();
    this._loading$.complete();

    this._destroy$.next();
    this._destroy$.complete();

    this._scrollService.remove(`fs-infinite-scroll-${this._id}`);
  }

  public reload(): void {
    this._element.scroll({ top: 0 });
    this._complete = false;

    this._loaded$
      .pipe(
        take(1),
        delay(300),
        filter(() => !this._complete && !this._isContainerFill()),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this.reload();
      });

    this._load$.next();
  }

  public loadStarted(): void {
    this._loading$.next(true);
  }

  public loadCompleted(): void {
    this._loading$.next(false);
    this._loaded$.next(true);
  }

  public complete(): void {
    this._complete = true;
  }

  private _initSubscriptions(): void {
    const scroll$ = this._scrollRef.scrolledDown$.pipe(
      filter(() => !this.isLoading && !this.isComplete),
      takeUntil(this._destroy$),
    );

    const resize$ = fromEvent(window, 'resize').pipe(
      filter(() => !this.isLoading && !this.isComplete),
      filter(() => !this._isContainerFill()),
      throttleTime(1000),
      takeUntil(this._destroy$),
    );

    merge(scroll$, resize$)
      .pipe(throttleTime(1000), takeUntil(this._destroy$))
      .subscribe(() => this._load$.next());

    this._loading$.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      this.isLoading = value;
      this._changeDetectorRef.markForCheck();
    });
  }

  private _isContainerFill(): boolean {
    return (
      this._element.scrollHeight > this._element.clientHeight ||
      (this._element.scrollHeight === 0 && this._element.clientHeight === 0)
    );
  }
}
